import type { Options } from 'react-to-pdf';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const DEFAULT_PAGE_CONTENT_RATIO = 277 / 190;
const MIN_TIMELINE_CHUNK_HEIGHT = 240;
const PAGE_TOP_GAP = 8;
const SECTION_GAP = 12;
const TIMELINE_PAGE_TOP_GAP = 4;
const TIMELINE_SECTION_GAP = 8;
const CAPTURE_SCALE = 0.8;
const CAPTURE_WINDOW_WIDTH = 1440;
const CAPTURE_WINDOW_HEIGHT = 900;
const TIMELINE_ROWS_PER_PAGE = 20;

function ensurePrintContainer(): HTMLElement {
  let printContainer = document.getElementById(
    'for-pdf-print',
  ) as HTMLElement | null;
  if (!printContainer) {
    printContainer = document.createElement('div');
    printContainer.id = 'for-pdf-print';
    printContainer.style.position = 'absolute';
    printContainer.style.top = '-9999px';
    printContainer.style.left = '-9999px';
    printContainer.style.backgroundColor = '#fff';
    document.body.appendChild(printContainer);
  }
  return printContainer!;
}

function createPrintPage(minHeight?: number): HTMLDivElement {
  const page = document.createElement('div');
  page.style.width = '100%';
  page.style.display = 'block';
  page.style.boxSizing = 'border-box';
  page.style.backgroundColor = '#fff';
  page.style.overflow = 'hidden';
  page.style.breakInside = 'avoid';
  page.style.pageBreakInside = 'avoid';
  if (typeof minHeight === 'number' && minHeight > 0) {
    page.style.minHeight = `${Math.round(minHeight)}px`;
  }
  return page;
}

function getElementWidth(element: HTMLElement | null | undefined): number {
  if (!element) return 0;
  return element.clientWidth || element.scrollWidth || 0;
}

function getElementHeight(element: HTMLElement | null | undefined): number {
  if (!element) return 0;
  return element.scrollHeight || element.clientHeight || 0;
}

function getPageHeight(
  pageWidth: number,
  pageContentRatio = DEFAULT_PAGE_CONTENT_RATIO,
): number {
  if (pageContentRatio <= 0)
    return Math.ceil(pageWidth / DEFAULT_PAGE_CONTENT_RATIO);
  return Math.ceil(pageWidth / pageContentRatio);
}

function createImageFromCanvas(
  canvas: HTMLCanvasElement,
  marginTop = 0,
): HTMLImageElement {
  const img = document.createElement('img');
  img.src = canvas.toDataURL('image/png');
  img.style.width = '100%';
  img.style.display = 'block';
  if (marginTop > 0) {
    img.style.marginTop = `${marginTop}px`;
  }
  return img;
}

function getPdfMargins(
  margin?:
    | number
    | { top?: number; right?: number; bottom?: number; left?: number },
) {
  if (typeof margin === 'number') {
    return { top: margin, right: margin, bottom: margin, left: margin };
  }

  return {
    top: margin?.top ?? 0,
    right: margin?.right ?? 0,
    bottom: margin?.bottom ?? 0,
    left: margin?.left ?? 0,
  };
}

function getRelativeTop(element: HTMLElement, ancestor: HTMLElement): number {
  const elementRect = element.getBoundingClientRect();
  const ancestorRect = ancestor.getBoundingClientRect();
  return Math.round(elementRect.top - ancestorRect.top);
}

async function captureElement(
  element: HTMLElement,
  options: Partial<Parameters<typeof html2canvas>[1]> = {},
): Promise<HTMLCanvasElement> {
  return html2canvas(element, {
    scale: CAPTURE_SCALE,
    useCORS: true,
    backgroundColor: '#fff',
    width: getElementWidth(element),
    height: getElementHeight(element),
    windowWidth: CAPTURE_WINDOW_WIDTH,
    windowHeight: CAPTURE_WINDOW_HEIGHT,
    scrollX: 0,
    scrollY: 0,
    logging: false,
    ...options,
  });
}

function applyProfileBarCloneStyles(node: Document): void {
  const allBtnEls = Array.from(node.querySelectorAll('.btn, button'));
  allBtnEls.forEach((el) => {
    (el as HTMLElement).style.display = 'none';
  });

  const hiddenEls = Array.from(node.querySelectorAll('.hide-in-pdf'));
  hiddenEls.forEach((el) => {
    (el as HTMLElement).style.display = 'none';
  });

  const detailsCol = node.querySelector(
    '.profilebar-details-col',
  ) as HTMLElement | null;
  if (detailsCol) {
    detailsCol.classList.remove('col-lg-8');
    detailsCol.classList.add('col-lg-10');
  }
}

function applyTimelineCloneStyles(node: Document): void {
  const hiddenEls = Array.from(node.querySelectorAll('.hide-in-pdf'));
  hiddenEls.forEach((el) => {
    (el as HTMLElement).style.display = 'none';
  });

  const allBadgeWhiteEls = Array.from(node.querySelectorAll('.badge-white'));
  allBadgeWhiteEls.forEach((el) => {
    (el as HTMLElement).style.width = '28px';
    (el as HTMLElement).style.height = '28px';
    (el as HTMLElement).style.fontSize = '12px';
    (el as HTMLElement).style.color = 'black';
  });
}

function prepareAdditionalSection(clonedEl: HTMLElement): HTMLElement {
  if (clonedEl.classList.contains('pt-4')) {
    clonedEl.classList.remove('pt-4');
    clonedEl.classList.add('pt-5');
  }

  const standardFontSizeEls = clonedEl.querySelectorAll(
    '.patient-footer-header',
  );
  standardFontSizeEls.forEach((el) => {
    (el as HTMLElement).style.fontSize = '14px';
  });

  const labelEls = Array.from(clonedEl.querySelectorAll('.form-label'));
  const pdfFontSizeEls = Array.from(
    clonedEl.querySelectorAll('.pdf-font-size-25'),
  );
  const allEls = [...labelEls, ...pdfFontSizeEls];
  allEls.forEach((el) => {
    (el as HTMLElement).style.setProperty('font-size', '14px', 'important');
    (el as HTMLElement).style.whiteSpace = 'nowrap';
  });

  const careProgressEls = Array.from(clonedEl.querySelectorAll('.labelRow'));
  careProgressEls.forEach((el) => {
    (el as HTMLElement).style.marginRight = '10px';
  });

  const allBtnEls = Array.from(clonedEl.querySelectorAll('.btn, button'));
  allBtnEls.forEach((el) => {
    (el as HTMLElement).style.display = 'none';
  });

  return clonedEl;
}

function getTimelineBreakpoints(
  timelineCalendarEl: HTMLElement,
  bodyOffsetTop: number,
  bodyHeight: number,
): { boundaries: number[]; avoidEndBoundaryIndices: Set<number> } {
  const sidebarRowEls = Array.from(
    timelineCalendarEl.querySelectorAll('.rct-sidebar .rct-sidebar-row'),
  ) as HTMLElement[];
  const fallbackRowEls = Array.from(
    timelineCalendarEl.querySelectorAll('.rct-horizontal-lines > div'),
  ) as HTMLElement[];
  const rowEls = sidebarRowEls.length > 0 ? sidebarRowEls : fallbackRowEls;

  if (rowEls.length === 0) {
    return { boundaries: [bodyHeight], avoidEndBoundaryIndices: new Set() };
  }

  const rawStarts = rowEls.map((rowEl) =>
    Math.max(0, getRelativeTop(rowEl, timelineCalendarEl) - bodyOffsetTop),
  );
  const baseStart = rawStarts[0] ?? 0;
  const normalizedStarts = rawStarts
    .map((value) => Math.max(0, Math.round(value - baseStart)))
    .filter((value) => value >= 0 && value < bodyHeight);

  const boundaries = [
    0,
    ...normalizedStarts.slice(1),
    Math.max(0, Math.round(bodyHeight)),
  ]
    .filter((value, index, array) => index === 0 || value > array[index - 1])
    .filter((value) => value >= 0 && value <= bodyHeight);

  const avoidEndBoundaryIndices = new Set<number>();
  if (sidebarRowEls.length > 0) {
    for (let index = 1; index < sidebarRowEls.length; index += 1) {
      const prevRowEl = sidebarRowEls[index - 1];
      const currentRowEl = sidebarRowEls[index];
      const prevHasToggle = !!prevRowEl.querySelector('.arrowCollapse');
      const currentIsChild = !!currentRowEl.querySelector('.labelGrey');
      if (prevHasToggle && currentIsChild) {
        avoidEndBoundaryIndices.add(index);
      }
    }
  }

  return { boundaries, avoidEndBoundaryIndices };
}

function buildTimelineChunks(
  bodyHeight: number,
  firstPageHeight: number,
  nextPageHeight: number,
  boundaries: number[],
  avoidEndBoundaryIndices: Set<number>,
  rowsPerPage?: number,
): Array<{ start: number; height: number }> {
  const chunks: Array<{ start: number; height: number }> = [];
  let currentStart = 0;
  let chunkIndex = 0;
  let boundaryPointer = 0;

  while (currentStart < bodyHeight && boundaryPointer < boundaries.length) {
    const maxChunkHeight = chunkIndex === 0 ? firstPageHeight : nextPageHeight;
    const startForChunk = currentStart;
    const targetEnd = Math.min(bodyHeight, currentStart + maxChunkHeight);

    const startBoundaryPointer = boundaryPointer;
    let endIndexByHeight = startBoundaryPointer;
    for (
      let index = boundaries.length - 1;
      index >= startBoundaryPointer;
      index -= 1
    ) {
      if (boundaries[index] <= targetEnd && boundaries[index] > startForChunk) {
        endIndexByHeight = index;
        break;
      }
    }

    const endIndexByRowsRaw =
      typeof rowsPerPage === 'number' && rowsPerPage > 0
        ? Math.min(boundaries.length - 1, startBoundaryPointer + rowsPerPage)
        : endIndexByHeight;

    let resolvedEndIndex = Math.min(endIndexByHeight, endIndexByRowsRaw);
    if (resolvedEndIndex <= startBoundaryPointer) {
      resolvedEndIndex = Math.min(
        startBoundaryPointer + 1,
        boundaries.length - 1,
      );
    }

    while (
      avoidEndBoundaryIndices.has(resolvedEndIndex) &&
      resolvedEndIndex > startBoundaryPointer + 1
    ) {
      resolvedEndIndex -= 1;
    }

    let resolvedEnd = boundaries[resolvedEndIndex];
    if (resolvedEnd - startForChunk < MIN_TIMELINE_CHUNK_HEIGHT) {
      const forcedEndIndex = Math.min(endIndexByHeight, boundaries.length - 1);
      const forcedEnd = boundaries[forcedEndIndex];
      if (forcedEnd > resolvedEnd) {
        resolvedEndIndex = forcedEndIndex;
        resolvedEnd = forcedEnd;
      }
    }

    chunks.push({
      start: startForChunk,
      height: resolvedEnd - startForChunk,
    });

    currentStart = resolvedEnd;
    boundaryPointer = resolvedEndIndex;
    chunkIndex += 1;
  }

  return chunks;
}

async function appendTimelinePages(
  timelineEl: HTMLElement,
  printContainer: HTMLElement,
  pageHeight: number,
): Promise<void> {
  const timelineCalendarEl = timelineEl.querySelector(
    '.react-calendar-timeline',
  ) as HTMLElement | null;

  if (!timelineCalendarEl) {
    const fallbackPage = createPrintPage(pageHeight);
    if (printContainer.childElementCount > 0) {
      fallbackPage.style.pageBreakBefore = 'always';
      fallbackPage.style.breakBefore = 'page';
    }

    const canvas = await captureElement(timelineEl, {
      ignoreElements: (el) =>
        el.classList.contains('hide-in-pdf') ||
        (el as HTMLElement).id === 'divOverHeader',
      onclone: (node) => {
        applyTimelineCloneStyles(node);
        const clonedTimelineEl = node.getElementById(
          'patient-timeline',
        ) as HTMLElement | null;
        if (!clonedTimelineEl) return;
        const clonedCalendarEl = clonedTimelineEl.querySelector(
          '.react-calendar-timeline',
        ) as HTMLElement | null;
        if (!clonedCalendarEl) return;
        const clonedScrollEl = clonedCalendarEl.querySelector(
          '.rct-scroll',
        ) as HTMLElement | null;
        if (clonedScrollEl) {
          clonedScrollEl.style.overflow = 'visible';
        }
        clonedCalendarEl.style.height = 'auto';
      },
    });
    fallbackPage.appendChild(createImageFromCanvas(canvas, PAGE_TOP_GAP));
    printContainer.appendChild(fallbackPage);
    return;
  }

  const headerEl = timelineCalendarEl.querySelector(
    '.rct-header-root',
  ) as HTMLElement | null;

  const headerCanvas = headerEl
    ? await captureElement(headerEl, {
        ignoreElements: (el) => el.classList.contains('hide-in-pdf'),
        onclone: applyTimelineCloneStyles,
      })
    : null;

  const headerHeight = headerEl
    ? Math.round(headerEl.getBoundingClientRect().height)
    : 0;
  const headerOffsetTop = headerEl
    ? getRelativeTop(headerEl, timelineCalendarEl)
    : 0;
  const bodyOffsetTop = headerOffsetTop + headerHeight;
  const bodyHeight = Math.max(
    0,
    getElementHeight(timelineCalendarEl) - bodyOffsetTop,
  );

  if (bodyHeight === 0) {
    const emptyTimelinePage = createPrintPage(pageHeight);
    if (printContainer.childElementCount > 0) {
      emptyTimelinePage.style.pageBreakBefore = 'always';
      emptyTimelinePage.style.breakBefore = 'page';
    }
    if (headerCanvas) {
      emptyTimelinePage.appendChild(
        createImageFromCanvas(headerCanvas, TIMELINE_PAGE_TOP_GAP),
      );
    }
    printContainer.appendChild(emptyTimelinePage);
    return;
  }

  const { boundaries, avoidEndBoundaryIndices } = getTimelineBreakpoints(
    timelineCalendarEl,
    bodyOffsetTop,
    bodyHeight,
  );
  const firstPageAvailableHeight = Math.max(
    MIN_TIMELINE_CHUNK_HEIGHT,
    pageHeight - headerHeight - TIMELINE_PAGE_TOP_GAP - TIMELINE_SECTION_GAP,
  );
  const nextPageAvailableHeight = firstPageAvailableHeight;
  const timelineChunks = buildTimelineChunks(
    bodyHeight,
    firstPageAvailableHeight,
    nextPageAvailableHeight,
    boundaries,
    avoidEndBoundaryIndices,
    TIMELINE_ROWS_PER_PAGE,
  );

  const timelineCalendarFullHeight = getElementHeight(timelineCalendarEl);

  for (let index = 0; index < timelineChunks.length; index += 1) {
    const chunk = timelineChunks[index];
    const timelinePage = createPrintPage(pageHeight);
    if (printContainer.childElementCount > 0) {
      timelinePage.style.pageBreakBefore = 'always';
      timelinePage.style.breakBefore = 'page';
    }

    if (headerCanvas) {
      timelinePage.appendChild(
        createImageFromCanvas(headerCanvas, TIMELINE_PAGE_TOP_GAP),
      );
    }

    const bodyCanvas = await captureElement(timelineCalendarEl, {
      y: bodyOffsetTop + chunk.start,
      height: chunk.height,
      ignoreElements: (el) => el.classList.contains('hide-in-pdf'),
      onclone: (node) => {
        applyTimelineCloneStyles(node);
        const clonedCalendarEl = node.querySelector(
          '.react-calendar-timeline',
        ) as HTMLElement | null;
        if (!clonedCalendarEl) return;
        const clonedScrollEl = clonedCalendarEl.querySelector(
          '.rct-scroll',
        ) as HTMLElement | null;
        if (clonedScrollEl) {
          clonedScrollEl.style.overflow = 'visible';
          clonedScrollEl.style.height = `${timelineCalendarFullHeight}px`;
          clonedScrollEl.scrollTop = 0;
        }
        clonedCalendarEl.style.height = `${timelineCalendarFullHeight}px`;
      },
    });

    timelinePage.appendChild(
      createImageFromCanvas(
        bodyCanvas,
        headerCanvas ? TIMELINE_SECTION_GAP : TIMELINE_PAGE_TOP_GAP,
      ),
    );
    printContainer.appendChild(timelinePage);
  }
}

export const buildPrintContainer = async (
  pageContentRatio?: number,
): Promise<HTMLElement | null> => {
  const timelineEl = document.getElementById('patient-timeline');
  const additionalEl = document.getElementById('additional-section');
  const profileBarEl = document.getElementById('profileBar');
  const printContainer = ensurePrintContainer();

  if (!timelineEl || !printContainer) return null;

  const additionalRoot =
    (additionalEl?.parentElement as HTMLElement | null) || additionalEl;
  const printWidth = Math.max(
    getElementWidth(profileBarEl),
    getElementWidth(additionalRoot),
    getElementWidth(timelineEl),
  );
  const pageHeight = getPageHeight(
    printWidth || getElementWidth(timelineEl),
    pageContentRatio,
  );
  const firstPage = createPrintPage(pageHeight);

  printContainer.innerHTML = '';
  if (printWidth > 0) {
    printContainer.style.width = `${printWidth}px`;
  }

  if (profileBarEl) {
    const canvas = await captureElement(profileBarEl, {
      ignoreElements: (el) =>
        el.classList.contains('hide-in-pdf') ||
        el.classList.contains('btn') ||
        el.tagName.toLowerCase() === 'button',
      onclone: applyProfileBarCloneStyles,
    });

    firstPage.appendChild(createImageFromCanvas(canvas, PAGE_TOP_GAP));
  }

  if (additionalRoot) {
    const clonedEl = prepareAdditionalSection(
      additionalRoot.cloneNode(true) as HTMLElement,
    );
    if (firstPage.childElementCount > 0) {
      clonedEl.style.marginTop = `${SECTION_GAP}px`;
    }
    firstPage.appendChild(clonedEl);
  }

  if (firstPage.childElementCount > 0) {
    firstPage.style.pageBreakAfter = 'always';
    firstPage.style.breakAfter = 'page';
    printContainer.appendChild(firstPage);
  }

  await appendTimelinePages(timelineEl, printContainer, pageHeight);
  return printContainer;
};

export const generatePatientPDF = async (options?: Options) => {
  const pdf = new jsPDF({
    orientation: options?.page?.orientation ?? 'portrait',
    unit: 'mm',
    format: options?.page?.format ?? 'a4',
    compress: options?.overrides?.pdf?.compress ?? true,
  });
  const margins = getPdfMargins(options?.page?.margin);
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const availableWidth = pageWidth - margins.left - margins.right;
  const availableHeight = pageHeight - margins.top - margins.bottom;
  const container = await buildPrintContainer(availableWidth / availableHeight);
  if (!container) return;
  const pageEls = Array.from(container.children) as HTMLElement[];

  for (let index = 0; index < pageEls.length; index += 1) {
    const pageEl = pageEls[index];
    const pageCanvas = await captureElement(pageEl, {
      backgroundColor: '#fff',
    });
    const widthRatio = availableWidth / pageCanvas.width;
    const heightRatio = availableHeight / pageCanvas.height;
    const ratio = Math.min(widthRatio, heightRatio);
    const renderWidth = pageCanvas.width * ratio;
    const renderHeight = pageCanvas.height * ratio;

    if (index > 0) {
      pdf.addPage();
    }

    pdf.addImage(
      pageCanvas.toDataURL('image/png', options?.canvas?.qualityRatio ?? 1),
      'PNG',
      margins.left,
      margins.top,
      renderWidth,
      renderHeight,
    );
  }

  if (options?.method === 'save') {
    pdf.save('patient-details.pdf');
    return;
  }

  const blobUrl = pdf.output('bloburl');
  window.open(blobUrl, '_blank');
};
